"use client"

import type React from "react"

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react"
import { motion } from "framer-motion"
import styles from "./search-bar.module.css"
import Search from "@/components/IconsTsx/Search"
import ArrowDown from "@/components/IconsTsx/ChevronDown"
import { BackgroundBlueCheckIcon } from "@/components/IconsTsx/BackgroundBlueCheckIcon";
import { usePlanes } from '@/hooks/usePlanes';
import { usePlanesOdont } from '@/hooks/usePlanesOdont';
import { useLocations } from '@/hooks/useLocations';
import { useSpecialties } from '@/hooks/useSpecialties';
import { fetchSearchResults, Professional, SearchFetchParams, SearchResults } from '@/hooks/useSearch';
import { useProfessionalSuggestions, MIN_SUGGESTION_LENGTH } from '@/hooks/useProfessionalSuggestions';
import { useProfessionalSpecialties } from '@/hooks/useProfessionalSpecialties';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { stripProfessionalTitle } from '@/lib/professionalName';
import {
  formatOdontPlansForDisplay,
  isCabaLocation,
  isConectaPlan,
  ODONT_PLAN_DISPLAY_NAMES,
  sortPlansConectaLast,
} from '@/lib/planFilters';
import {
  trackSearchTypeSelected,
  trackPlanSelected,
  trackLocationSelected,
  trackSpecialtySelected,
  trackSearchSubmitted,
  trackFilterOpened,
  trackFilterSearchTyped,
  trackFilterAbandoned,
  trackSearchResultsReturned,
  setCurrentSearchFilters,
  trackZeroResults,
  trackSearchRefinedFromZero,
} from '@/lib/mixpanelClient';
import { nearestLocality, normalizeLocalityName } from '@/lib/geo';

// Mapeo de códigos según buscarPorOpciones (copiado de useSpecialties)
const SEARCH_TYPE_CODES: Record<string, string> = {
  'Especialidad médica': 'C1',
  'Especialidad odontológica': 'C8',
  'Nombre del profesional': 'C1',
  'Salud mental': 'C1',
  'Atención de urgencia': 'C13',
  'Atención sanatorial': 'C5',
  'Estudio por imágenes': 'C10',
  'Laboratorios': 'C2',
  'Farmacias': 'C6',
  'Ópticas': 'C12'
};

const LocationCrosshairIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2v3M12 19v3M22 12h-3M5 12H2" />
    <circle cx="12" cy="12" r="8" />
  </svg>
);


export interface SelectedLocation {
  TIPOLOC: string
  ZONA: string
  USERLOCATION: string
  PROVINCIA: string
  SYSTEMLOCATION: string
}

interface SearchBarProps {
  onSearch?: (searchData: SearchData) => void
  onSearchResults?: (results: SearchResults) => void
  onSpecialtyChange?: (specialty: string) => void
  onCartillaChange?: (cartilla: { nombrePdf: string; url: string } | null) => void
  /** Plan elegido (código CARTILLA). Lo usa Salud mental para mostrar el
   *  contenido correcto (tradicional vs MS1/MS2). */
  onPlanChange?: (plan: string) => void
  onLocationSelect?: (location: SelectedLocation | null) => void
  /** Coordenadas reales del dispositivo cuando se usa "mi ubicación" (para
   *  centrar la cercanía en la posición exacta). null al limpiar la ubicación. */
  onUserCoords?: (coords: { lat: number; lng: number } | null) => void
  /** Avisa si la última búsqueda tuvo que ampliarse a nivel país porque la
   *  zona elegida no tenía resultados directos (fallback automático). Solo
   *  para el aviso "no encontramos resultados en la zona elegida". */
  onNationwideFallback?: (active: boolean) => void
  /** Avisa si la última búsqueda fue a nivel país (manual con "Todos" O el
   *  fallback automático) — a diferencia de onNationwideFallback, esto es
   *  true en los dos casos. Sirve para NO forzar el radio a 5km cuando la
   *  búsqueda ya es a propósito "todo el país" (si no, "Todos" no serviría
   *  de nada: el resultado nuevo volvería a taparse en 5km). */
  onNationwideSearch?: (active: boolean) => void
  className?: string
}

/** Métodos expuestos por ref para acciones disparadas desde fuera (ej. el
 *  botón "Todos" de los resultados, que vive en otro componente hermano). */
export interface SearchBarHandle {
  /** Repite la última búsqueda pero a nivel país: mismos plan/especialidad,
   *  ignorando la Pcia/Zona/localidad/barrio elegidos. */
  searchNationwide: () => void
}

export interface SearchData {
  especialidad: string
  plan: string
  nombreApellido: string
  especialidadSecundaria: string
  professionals?: Professional[]
}

// Definir la interfaz para los planes
interface Plan {
  CARTILLA: string;
  CRT_CODIGO: string;
  PLAN_VENTA: string;
  REFERENCIAS: string;
  displayName?: string; // Nombre para mostrar (sin "ODONTOLOGIA")
}

// Definir la interfaz para las localidades
interface Location {
  TIPOLOC: string;
  USERLOCATION: string;
  SYSTEMLOCATION: string;
  PROVINCIA: string;
  ZONA: string;
}

// Definir la interfaz para las especialidades
interface Specialty {
  ESPECIALIDAD: string;
  CODIGO: string;
}

const buscarPorOpciones = [
  "Especialidad médica",
  "Especialidad odontológica",
  "Nombre del profesional",
  "Salud mental",
  "Atención de urgencia",
  "Atención sanatorial",
  "Estudio por imágenes",
  "Laboratorios",
  "Farmacias",
  "Ópticas"
];

const ESPECIALIDADES_CON_LOCATION_Y_SECUNDARIA = new Set([
  "Especialidad médica",
  "Especialidad odontológica",
  "Salud mental",
  "Atención de urgencia",
  "Atención sanatorial",
  "Estudio por imágenes",
]);
const ESPECIALIDADES_SOLO_LOCATION_WIDE = new Set([
  "Laboratorios",
  "Farmacias",
  "Ópticas",
]);

// Devuelve las claves de filtros cuyo valor cambió entre dos búsquedas
// (para search_refined_from_zero: qué modificó el usuario tras un cero).
const changedFiltrosKeys = (a: Record<string, string>, b: Record<string, string>): string[] =>
  Object.keys(b).filter((k) => a[k] !== b[k]);

// Consulta /api/locations y normaliza la respuesta a un array de Location.
// Extraído para reusar entre la búsqueda por nombre (GPS) y el fallback a
// Capital Federal cuando el plan es Conecta (VEN-395).
const fetchLocationsByText = async (plan: string, text: string): Promise<Location[]> => {
  const res = await fetch(`/api/locations?plan=${encodeURIComponent(plan)}&text=${encodeURIComponent(text)}`);
  const data = await res.json();
  return Array.isArray(data?.LOCALIDAD)
    ? data.LOCALIDAD.filter(Boolean)
    : data?.LOCALIDAD ? [data.LOCALIDAD] : [];
};

// Mapea una localidad a las props de tracking (provincia/localidad/zona).
// La localidad solo aplica a picks de tipo LOCALIDAD/BARRIO; para una ZONA
// queda vacía (solo tenemos provincia y código de zona).
const toLocationTrackProps = (loc: {
  PROVINCIA?: string; SYSTEMLOCATION?: string; ZONA?: string; TIPOLOC?: string;
}) => {
  // "=" es el sentinel de "vacío" del backend; no debe filtrarse al analytics.
  const clean = (v?: string) => {
    const t = v?.trim() || '';
    return t === '=' ? '' : t;
  };
  const tipo = (loc.TIPOLOC || '').trim().toUpperCase();
  const isLocalidad = tipo === 'LOCALIDAD' || tipo === 'BARRIO';
  return {
    provincia: clean(loc.PROVINCIA),
    localidad: isLocalidad ? clean(loc.SYSTEMLOCATION) : '',
    zona: clean(loc.ZONA),
  };
};

/**
 * Prioridad: la zona elegida. Si esa búsqueda puntual no encontró nada (y no
 * era ya una búsqueda país), reintenta sin filtro de ubicación. Devuelve los
 * resultados a mostrar (los nacionales solo si trajeron algo) y si tuvo que
 * ampliar. Extraída de `handleSearch` para mantener su complejidad cognitiva
 * ≤ 15 (Sonar S3776).
 */
async function ampliarANivelPaisSiVacio(
  nationwide: boolean,
  results: SearchResults,
  selectedLocationData: SelectedLocation | undefined,
  baseParams: Omit<SearchFetchParams, 'locationData'>,
): Promise<{ results: SearchResults; usedFallback: boolean }> {
  if (nationwide || results.totalResults > 0 || !selectedLocationData) {
    return { results, usedFallback: false };
  }
  const nationwideResults = await fetchSearchResults({ ...baseParams, locationData: undefined });
  return nationwideResults.totalResults > 0
    ? { results: nationwideResults, usedFallback: true }
    : { results, usedFallback: false };
}

/** Info del PDF de cartilla para el plan/zona dados. `null` si no hay o falla. */
async function fetchCartillaInfo(
  plan: string,
  zona: string,
): Promise<{ nombrePdf: string; url: string } | null> {
  try {
    const cartillaParams = new URLSearchParams({ plan, zona });
    const cartillaResponse = await fetch(`/api/cartilla?${cartillaParams.toString()}`);
    if (!cartillaResponse.ok) return null;
    const cartillaResult = await cartillaResponse.json();
    return cartillaResult.cartilla ?? null;
  } catch (cartillaError) {
    console.error('Error al obtener cartilla:', cartillaError);
    return null;
  }
}

export const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(function SearchBar(
  { onSearch, onSearchResults, onSpecialtyChange, onCartillaChange, onPlanChange, onLocationSelect, onUserCoords, onNationwideFallback, onNationwideSearch, className = "" },
  ref,
) {
  const [searchData, setSearchData] = useState<SearchData>({
    especialidad: "",
    plan: "",
    nombreApellido: "",
    especialidadSecundaria: "",
  })

  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false)
  const [showProfessionalSuggestions, setShowProfessionalSuggestions] = useState(false)
  const [selectedLocationData, setSelectedLocationData] = useState<{
    TIPOLOC: string;
    ZONA: string;
    USERLOCATION: string;
    PROVINCIA: string;
    SYSTEMLOCATION: string;
  } | undefined>(undefined)
  const [selectedProfessionalName, setSelectedProfessionalName] = useState<string | undefined>(undefined)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoadingProfessionals, setIsLoadingProfessionals] = useState(false)
  const [isLocating, setIsLocating] = useState(false)
  const [geoError, setGeoError] = useState<string>("")
  const [dropdownFilter, setDropdownFilter] = useState("")
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)

  // Función para determinar si el botón de búsqueda debe estar deshabilitado
  const isSearchButtonDisabled = () => {
    if (isDisabled || isLoadingProfessionals) {
      return true;
    }

    if (!searchData.especialidad || !searchData.plan) {
      return true;
    }

    const hasLocation = !!selectedLocationData;
    const hasProfessionalName = !!(
      selectedProfessionalName?.trim() || searchData.nombreApellido.trim()
    );

    if (ESPECIALIDADES_CON_LOCATION_Y_SECUNDARIA.has(searchData.especialidad)) {
      return !hasLocation;
    }

    if (searchData.especialidad === "Nombre del profesional") {
      return !hasProfessionalName;
    }

    if (ESPECIALIDADES_SOLO_LOCATION_WIDE.has(searchData.especialidad)) {
      return !hasLocation;
    }

    return true;
  };

  const { data: planes, isLoading: isLoadingPlanes, error: errorPlanes } = usePlanes();
  const { data: planesOdont, isLoading: isLoadingPlanesOdont, error: errorPlanesOdont } = usePlanesOdont();

  // Hook para búsqueda de localidades
  const { data: locations, isLoading: isLoadingLocations } = useLocations(
    searchData.plan,
    searchData.nombreApellido
  );

  // Hook para búsqueda de especialidades. El nombre de la especialidad no
  // depende de la ubicación, pero su CÓDIGO interno sí (useSpecialties le pide
  // al backend el código correcto para la zona/provincia/localidad elegida).
  // Pasar undefined acá traía un código "genérico" que no siempre coincidía
  // con el válido para la ubicación real, y la búsqueda posterior (que sí es
  // exacta) volvía vacía (VEN-394).
  const { data: specialties, isLoading: isLoadingSpecialties } = useSpecialties({
    plan: searchData.plan,
    searchType: searchData.especialidad,
    locationData: selectedLocationData
  });

  // Sugerencias predictivas al buscar por nombre de profesional. Se consulta
  // (con debounce) solo cuando corresponde ese tipo de búsqueda, hay un plan y
  // todavía no se seleccionó un nombre.
  const isNameSearch = searchData.especialidad === "Nombre del profesional";
  const debouncedName = useDebouncedValue(
    isNameSearch && !selectedProfessionalName ? searchData.nombreApellido : "",
    300
  );
  const { data: professionalSuggestions, isLoading: isLoadingSuggestions } = useProfessionalSuggestions({
    plan: searchData.plan,
    text: debouncedName,
    especialidadNombre: searchData.especialidadSecundaria,
    enabled: isNameSearch && !selectedProfessionalName,
  });

  // Al seleccionar un profesional, el dropdown de Especialidad se acota a las
  // especialidades que esa persona efectivamente atiende (derivadas de sus
  // propios resultados), en vez de la lista genérica del plan.
  const professionalSelected = isNameSearch && !!selectedProfessionalName;
  const { data: professionalSpecialties, isLoading: isLoadingProfessionalSpecialties } =
    useProfessionalSpecialties({
      plan: searchData.plan,
      professionalName: selectedProfessionalName,
      enabled: professionalSelected,
    });

  // Fuente efectiva de especialidades: las del profesional cuando hay uno
  // elegido; si no, la lista genérica de useSpecialties.
  const specialtiesSource: Specialty[] = professionalSelected
    ? (professionalSpecialties ?? [])
    : (specialties ?? []);
  const isLoadingSpecialtiesSource = professionalSelected
    ? isLoadingProfessionalSpecialties
    : isLoadingSpecialties;

  // Modelo "centro de cercanía": acotamos la consulta al backend con lo que el
  // usuario eligió (localidad/zona por su código de ZONA, o la provincia por
  // nombre) y el orden por distancia se hace en el cliente. Antes se forzaba
  // todo a "PROVINCIA" descartando el código de zona, y como el nombre de
  // provincia tiene espacios terminaba enviándose "=" → traía TODO el país.
  const handleInputChange = (field: keyof SearchData, value: string) => {
    setSearchData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // --- Tracking de dropdowns (Pantalla 2): sesión del filtro abierto ---
  // Registra apertura (filter_opened), abandono sin elegir (filter_abandoned)
  // y si el usuario tipeó/scrolleó mientras estaba abierto, tanto en el campo
  // Pcia/Zona como en el buscador interno de los dropdowns.
  const filterSessionRef = useRef<null | {
    filtro: string; openedAt: number; typed: boolean; scrolled: boolean;
    peakTyped?: string; peakHuboMatch?: boolean;
  }>(null);

  // Filtros de la búsqueda anterior SI dio cero (para search_refined_from_zero, #22).
  const prevZeroFiltrosRef = useRef<Record<string, string> | null>(null);

  // openDropdown interno → nombre de filtro del tracking plan.
  const FILTRO_BY_DROPDOWN: Record<string, string> = {
    especialidad: 'buscar_por',
    plan: 'plan',
    especialidadSecundaria: 'especialidad',
  };

  const getOtherFiltersState = (currentFiltro: string): Record<string, boolean> => {
    const state: Record<string, boolean> = {
      tipo_busqueda: !!searchData.especialidad,
      plan: !!searchData.plan,
      especialidad: !!searchData.especialidadSecundaria,
      ubicacion: !!selectedLocationData,
    };
    const keyByFiltro: Record<string, string> = {
      buscar_por: 'tipo_busqueda', plan: 'plan', especialidad: 'especialidad', ubicacion: 'ubicacion',
    };
    delete state[keyByFiltro[currentFiltro]];
    return state;
  };

  const openFilterSession = (filtro: string) => {
    if (filterSessionRef.current?.filtro === filtro) return; // ya abierto
    closeFilterSession(); // abandona la sesión previa si quedó abierta
    filterSessionRef.current = { filtro, openedAt: Date.now(), typed: false, scrolled: false };
    trackFilterOpened({ filtro, estadoOtrosFiltros: getOtherFiltersState(filtro) }); // #7
  };

  // Emite UN filter_search_typed (#8) por sesión con el intento MÁS LARGO/completo
  // (no el último tecleado): si el usuario refina hasta uno que matchea, el último
  // texto tendría hubo_match=true y perderíamos la búsqueda que NO encontró (el oro).
  const emitFilterTypedFromSession = () => {
    const s = filterSessionRef.current;
    if (s?.peakTyped) {
      trackFilterSearchTyped({ filtro: s.filtro, textoTipeado: s.peakTyped, huboMatch: !!s.peakHuboMatch });
    }
  };

  // Registra un tecleo en la sesión abierta, guardando el intento más largo.
  const recordFilterTyped = (texto: string, huboMatch: boolean) => {
    const s = filterSessionRef.current;
    if (!s || !texto) return;
    s.typed = true;
    if (!s.peakTyped || texto.length > s.peakTyped.length) {
      s.peakTyped = texto;
      s.peakHuboMatch = huboMatch;
    }
  };

  const closeFilterSession = () => {
    const s = filterSessionRef.current;
    if (!s) return;
    emitFilterTypedFromSession(); // #8 (una vez, intento más completo)
    filterSessionRef.current = null;
    trackFilterAbandoned({ // #9
      filtro: s.filtro,
      tiempoAbierto: Math.round((Date.now() - s.openedAt) / 1000),
      scrolleo: s.scrolled,
      tipeo: s.typed,
    });
  };

  // Hubo selección → emite el typed acumulado y termina la sesión sin abandono.
  const endFilterSessionSelected = () => {
    emitFilterTypedFromSession(); // #8
    filterSessionRef.current = null;
  };
  const markFilterTyped = () => { if (filterSessionRef.current) filterSessionRef.current.typed = true; };
  const markFilterScrolled = () => { if (filterSessionRef.current) filterSessionRef.current.scrolled = true; };

  // Tipeo en el campo Pcia/Zona (ubicacion), debounced. hubo_match aproximado
  // según las localidades disponibles al momento de disparar.
  useEffect(() => {
    const texto = searchData.nombreApellido.trim();
    if (!texto || selectedLocationData || filterSessionRef.current?.filtro !== 'ubicacion') return;
    const timer = setTimeout(() => {
      if (filterSessionRef.current?.filtro !== 'ubicacion') return;
      recordFilterTyped(texto, (locations?.length ?? 0) > 0);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData.nombreApellido]);

  const handleDropdownToggle = (dropdown: string) => {
    // Abrir un dropdown cierra las sugerencias de ubicación (un solo overlay activo).
    setShowLocationSuggestions(false)
    setDropdownFilter("")
    if (openDropdown === dropdown) {
      setOpenDropdown(null)
      closeFilterSession() // se cierra el mismo dropdown sin elegir → abandono
    } else {
      setOpenDropdown(dropdown)
      openFilterSession(FILTRO_BY_DROPDOWN[dropdown] ?? dropdown)
    }
  }

  // Determina si un tipo de búsqueda utiliza el campo Pcia/Zona (localidad).
  const usesLocation = (type: string) =>
    type === "" ||
    (type !== "Salud mental" &&
      (ESPECIALIDADES_CON_LOCATION_Y_SECUNDARIA.has(type) ||
        ESPECIALIDADES_SOLO_LOCATION_WIDE.has(type)));

  // VEN-395: Conecta solo tiene red de prestadores en CABA, así que Pcia/Zona
  // se fija automáticamente en Capital Federal en cuanto se elige el plan
  // (ver useEffect más abajo) y el campo queda bloqueado: no tiene sentido
  // dejar tipear o re-elegir una zona que igual no es válida.
  const autoSelectCabaForConecta = async (plan: string) => {
    try {
      const items = await fetchLocationsByText(plan, "Capital Federal");
      const match = items.find(isCabaLocation);
      if (!match) return;
      const selected = {
        TIPOLOC: match.TIPOLOC,
        ZONA: match.ZONA,
        USERLOCATION: match.USERLOCATION,
        PROVINCIA: match.PROVINCIA,
        SYSTEMLOCATION: match.SYSTEMLOCATION,
      };
      setSelectedLocationData(selected);
      onLocationSelect?.(selected);
      onUserCoords?.(null);
      setSearchData(prev => ({ ...prev, nombreApellido: formatLocationDisplay(match) }));
    } catch {
      setGeoError("No pudimos fijar Capital Federal automáticamente. Reintentá eligiendo el plan de nuevo.");
    }
  };

  // Dispara el auto-fijado apenas el plan vigente es Conecta (y el tipo de
  // búsqueda usa Pcia/Zona). Cubre tanto la selección directa del plan como
  // los casos en que el plan Conecta "sobrevive" a un cambio de especialidad
  // pero la ubicación se había limpiado.
  useEffect(() => {
    if (!isConectaPlan(searchData.plan) || !usesLocation(searchData.especialidad)) return;
    if (selectedLocationData && isCabaLocation(selectedLocationData)) return;
    autoSelectCabaForConecta(searchData.plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData.plan, searchData.especialidad, selectedLocationData]);

  const handleOptionSelect = (field: keyof SearchData, value: string) => {
    // Al cambiar la especialidad ("Buscar por") preservamos Plan y Pcia/Zona
    // para no obligar al usuario a re-elegirlos (reduce fricción). Solo se
    // resetea lo que depende del tipo de búsqueda.
    if (field === "especialidad") {
      trackSearchTypeSelected(value); // #2 search_type_selected
      const prevType = searchData.especialidad;

      // Los planes odontológicos son un universo distinto a los médicos: si se
      // cruza ese límite, el plan elegido ya no es válido y hay que limpiarlo.
      const crossesOdont =
        (prevType === "Especialidad odontológica") !== (value === "Especialidad odontológica");
      const keepPlan = !crossesOdont;

      // Conservamos la ubicación solo si el nuevo tipo usa Pcia/Zona y ya había
      // una localidad elegida. nombreApellido está sobrecargado (guarda el texto
      // de la localidad para tipos con ubicación y el nombre para "Nombre del
      // profesional"), por eso solo preservamos su valor cuando keepLocation.
      const keepLocation = usesLocation(value) && !!selectedLocationData;

      setSearchData(prev => ({
        ...prev,
        especialidad: value,
        plan: keepPlan ? prev.plan : "",
        nombreApellido: keepLocation ? prev.nombreApellido : "",
        especialidadSecundaria: "", // depende del tipo de búsqueda → se resetea
      }));

      if (!keepLocation) {
        setSelectedLocationData(undefined);
        onLocationSelect?.(null);
        onUserCoords?.(null);
      }
      setSelectedProfessionalName(undefined);
      setShowProfessionalSuggestions(false);
      setGeoError("");

      // Deshabilitar inputs cuando se selecciona "Salud mental"
      setIsDisabled(value === "Salud mental");

      // Notificar al componente padre sobre el cambio de especialidad
      onSpecialtyChange?.(value);
      // Avisar al padre qué plan quedó vigente (se conserva salvo que se cruce
      // el límite médico ↔ odontológico). Salud mental lo usa para su contenido.
      onPlanChange?.(keepPlan ? searchData.plan : "");
    } else {
      handleInputChange(field, value);
      if (field === "plan") {
        trackPlanSelected(value); // #3 plan_selected
        // Salud mental usa el plan para mostrar el contenido correcto.
        onPlanChange?.(value);
        // Al elegir un plan desaparece el aviso "Elegí un plan antes de usar tu ubicación".
        setGeoError("");
        // Si es Conecta, el useEffect de más arriba fija Capital Federal solo.
      }
      if (field === "especialidadSecundaria") {
        // #5 specialty_selected — con el buscador interno del dropdown, el
        // método es tipeo si hubo filtro y scroll si no.
        trackSpecialtySelected({ especialidad: value, metodo: dropdownFilter.trim() ? "tipeo" : "scroll" });
      }
    }
    endFilterSessionSelected() // hubo selección → no es abandono (#9)
    setDropdownFilter("")
    setOpenDropdown(null)
  }

  const handleSearch = async (opts?: { nationwide?: boolean }) => {
    const nationwide = opts?.nationwide ?? false;
    const loc = !nationwide && selectedLocationData
      ? toLocationTrackProps(selectedLocationData)
      : { provincia: '', localidad: '', zona: '' };
    const nombreProfesional = searchData.especialidad === "Nombre del profesional"
      ? (selectedProfessionalName || searchData.nombreApellido).trim()
      : '';
    const filtros = {
      plan: searchData.plan,
      provincia: loc.provincia,
      localidad: loc.localidad,
      zona: loc.zona,
      especialidad: searchData.especialidadSecundaria,
      search_type: searchData.especialidad,
    };
    setCurrentSearchFilters(filtros); // filtros para pdf/paginación (#13/#14)

    // #6 search_submitted — cierra el paso 1 del funnel.
    trackSearchSubmitted({
      plan: searchData.plan,
      provincia: loc.provincia,
      localidad: loc.localidad,
      zona: loc.zona,
      especialidad: searchData.especialidadSecundaria,
      searchType: searchData.especialidad,
      nombreProfesional: nombreProfesional || undefined,
    });

    setIsLoadingProfessionals(true);
    onNationwideFallback?.(false); // limpia el aviso de la búsqueda anterior
    onNationwideSearch?.(nationwide); // idem, se recalcula abajo con el resultado final
    const t0 = performance.now();

    const baseSearchParams = {
      plan: searchData.plan,
      searchTypeCode: SEARCH_TYPE_CODES[searchData.especialidad] || 'C1',
      especialidadNombre: searchData.especialidadSecundaria,
      vTexto: searchData.especialidad === "Nombre del profesional"
        ? stripProfessionalTitle(selectedProfessionalName || searchData.nombreApellido)
        : undefined,
    };

    try {
      const results = await fetchSearchResults({
        ...baseSearchParams,
        locationData: nationwide ? undefined : selectedLocationData,
      });

      // #10 search_results_returned — cobertura (incluye result_count=0 = zero results).
      trackSearchResultsReturned({
        resultCount: results.totalResults,
        tiempoMs: Math.round(performance.now() - t0),
        pagina: 1,
        filtros,
      });

      // #8 filter_search_typed (nombre_profesional) — en esta versión no hay lista
      // de sugerencias, así que el "match" se define por si la búsqueda encontró.
      if (nombreProfesional) {
        trackFilterSearchTyped({
          filtro: 'nombre_profesional',
          textoTipeado: nombreProfesional,
          huboMatch: results.totalResults > 0,
        });
      }

      // #21 zero_results (evento propio) y #22 search_refined_from_zero.
      const prevZero = prevZeroFiltrosRef.current;
      if (results.totalResults === 0) {
        trackZeroResults(filtros);
      }
      if (prevZero) {
        trackSearchRefinedFromZero({
          filtroQueCambio: changedFiltrosKeys(prevZero, filtros),
          encontro: results.totalResults > 0,
        });
      }
      prevZeroFiltrosRef.current = results.totalResults === 0 ? filtros : null;

      // Prioridad: la zona elegida. Si no encontró nada ahí, se amplía sola a
      // nivel país y se muestra/ordena automáticamente por cercanía — sin que
      // el usuario tenga que tocar nada (el orden por distancia lo hace
      // MedicalDirectory con el centro ya elegido, que no se toca acá).
      const { results: finalResults, usedFallback: usedNationwideFallback } =
        await ampliarANivelPaisSiVacio(nationwide, results, selectedLocationData, baseSearchParams);
      onNationwideFallback?.(usedNationwideFallback);
      // A diferencia de onNationwideFallback (solo el fallback automático),
      // esto es true también en el "Todos" manual — así el radio no se
      // resetea a 5km y tapa de nuevo lo que "Todos" acaba de traer.
      onNationwideSearch?.(nationwide || usedNationwideFallback);

      const zonaCartilla = (nationwide || usedNationwideFallback)
        ? '='
        : (selectedLocationData?.ZONA?.trim() || '=');
      const cartillaData = searchData.plan
        ? await fetchCartillaInfo(searchData.plan, zonaCartilla)
        : null;

      onCartillaChange?.(cartillaData);

      onSearch?.({ ...searchData, professionals: finalResults.professionals });
      onSearchResults?.(finalResults);
    } catch (error) {
      console.error('Error al buscar médicos:', error);
      onSearch?.({ ...searchData, professionals: [] });
      onSearchResults?.({
        professionals: [],
        totalResults: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 16
      });
      onCartillaChange?.(null);
      onNationwideFallback?.(false);
      onNationwideSearch?.(false);
    } finally {
      setIsLoadingProfessionals(false);
    }
  }

  useImperativeHandle(ref, () => ({
    searchNationwide: () => {
      void handleSearch({ nationwide: true });
    },
  }));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Si estamos en el modo "Nombre del profesional" y hay texto, seleccionar el nombre
      if (searchData.especialidad === "Nombre del profesional" && searchData.nombreApellido.trim()) {
        setSelectedProfessionalName(searchData.nombreApellido.trim());
      }
      // Si hay texto en el input y no hay una localidad seleccionada, buscar
      else if (searchData.nombreApellido && !selectedLocationData) {
        handleSearch();
      } else if (e.key === "Enter") {
        handleSearch();
      }
    }
  };

  // Función para manejar la selección de localidad.
  // La localidad ya no filtra ni condiciona la especialidad: solo define el
  // centro para ordenar por cercanía, así que NO limpiamos la especialidad.
  const handleLocationSelect = (location: Location) => {
    setGeoError("");
    setSearchData(prev => ({
      ...prev,
      nombreApellido: formatLocationDisplay(location)
    }));
    const selected = {
      TIPOLOC: location.TIPOLOC,
      ZONA: location.ZONA,
      USERLOCATION: location.USERLOCATION,
      PROVINCIA: location.PROVINCIA,
      SYSTEMLOCATION: location.SYSTEMLOCATION
    };
    setSelectedLocationData(selected);
    onLocationSelect?.(selected);
    // Pick manual: el centro de cercanía es la localidad elegida, no el GPS.
    onUserCoords?.(null);
    trackLocationSelected(toLocationTrackProps(location)); // #4 location_selected
    endFilterSessionSelected(); // eligió ubicación → no es abandono (#9)
    setShowLocationSuggestions(false);
  };

  // "Usar mi ubicación": detecta el dispositivo, deriva la localidad más cercana
  // de la tabla de centros, consulta su código de ZONA y acota la búsqueda a esa
  // zona ordenando por la posición EXACTA del usuario. Si falla o se deniega,
  // se muestra un mensaje y queda el campo Pcia/Zona para elegir manualmente.
  const handleUseMyLocation = () => {
    setGeoError("");
    if (!searchData.plan) {
      setGeoError("Elegí un plan antes de usar tu ubicación.");
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Tu navegador no permite geolocalización. Elegí una Pcia/Zona.");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          const nearest = nearestLocality(coords);
          if (!nearest) {
            setGeoError("No pudimos detectar tu zona. Elegí una Pcia/Zona.");
            return;
          }
          const queryName = nearest.name.split(" - ")[0].trim();
          const items = await fetchLocationsByText(searchData.plan, queryName);
          const target = normalizeLocalityName(nearest.name);
          let match: Location | undefined =
            items.find((i) => i?.TIPOLOC === "LOCALIDAD" && normalizeLocalityName(i.SYSTEMLOCATION) === target) ||
            items.find((i) => i?.TIPOLOC === "LOCALIDAD") ||
            items[0];

          // Conecta solo tiene red en CABA (VEN-395): el GPS puede caer fuera
          // (o el backend matchear una localidad de GBA lindera, ej. Florida).
          // En vez de bloquear al usuario, se auto-selecciona Capital Federal.
          const usedConectaFallback = isConectaPlan(searchData.plan) && (!match || !isCabaLocation(match));
          if (usedConectaFallback) {
            const cabaItems = await fetchLocationsByText(searchData.plan, "Capital Federal");
            match = cabaItems.find(isCabaLocation);
          }

          if (!match) {
            setGeoError("No pudimos detectar tu zona automáticamente. Elegí una Pcia/Zona.");
            return;
          }
          const selected = {
            TIPOLOC: match.TIPOLOC,
            ZONA: match.ZONA,
            USERLOCATION: match.USERLOCATION,
            PROVINCIA: match.PROVINCIA,
            SYSTEMLOCATION: match.SYSTEMLOCATION,
          };
          setSelectedLocationData(selected);
          onLocationSelect?.(selected);
          onUserCoords?.(coords);
          setSearchData((prev) => ({
            ...prev,
            nombreApellido: usedConectaFallback
              ? "Mi ubicación · Capital Federal"
              : `Mi ubicación · ${normalizePlanText(match.SYSTEMLOCATION)}`,
          }));
          setShowLocationSuggestions(false);
        } catch {
          setGeoError("No pudimos detectar tu zona. Elegí una Pcia/Zona.");
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setGeoError("No pudimos obtener tu ubicación. Elegí una Pcia/Zona.");
        setIsLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  // Función para renderizar el dropdown de especialidades
  const renderSpecialtiesDropdown = () => {
    const hasMinimumParams = !!(searchData.plan && searchData.especialidad);

    return (
      <motion.div
        className={styles.dropdownMenu}
        onScroll={markFilterScrolled}
        role="listbox"
        id="especialidadSecundaria-listbox"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        {showSpecialtySearch &&
          renderDropdownSearch("especialidadSecundaria-listbox", "Buscar especialidad…", filteredSpecialties, selectSpecialty)}

        {!hasMinimumParams ? (
          <div className={`${styles.dropdownOption} ${styles.dropdownHint}`}>
            Seleccioná un plan y tipo de búsqueda para ver especialidades
          </div>
        ) : isLoadingSpecialtiesSource ? (
          <div className={styles.dropdownOption}>Cargando especialidades...</div>
        ) : filteredSpecialties.length > 0 ? (
          <>
            {filteredSpecialties.map((specialty: Specialty, index: number) => (
              <div
                key={`${specialty.ESPECIALIDAD || 'unknown'}-${index}`}
                id={`especialidadSecundaria-opt-${index}`}
                role="option"
                aria-selected={searchData.especialidadSecundaria === specialty.ESPECIALIDAD}
                className={`${styles.dropdownOption} ${activeIndex === index ? styles.dropdownOptionActive : ''}`}
                onClick={() => selectSpecialty(specialty)}
                onMouseEnter={() => setActiveIndex(index)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                {normalizePlanText(specialty.ESPECIALIDAD)}
                {searchData.especialidadSecundaria === specialty.ESPECIALIDAD && (
                  <BackgroundBlueCheckIcon style={{ marginLeft: 8 }} />
                )}
              </div>
            ))}
          </>
        ) : (
          <div className={`${styles.dropdownOption} ${styles.dropdownHint}`}>
            {dropdownFilter
              ? `Sin resultados para "${dropdownFilter}"`
              : "No hay especialidades disponibles para esta búsqueda"}
          </div>
        )}
      </motion.div>
    );
  };

  // Cierra cualquier dropdown/sugerencia al hacer clic fuera del buscador o al
  // presionar Escape.
  useEffect(() => {
    const closeAll = () => {
      setOpenDropdown(null);
      setShowLocationSuggestions(false);
      setShowProfessionalSuggestions(false);
      // Cierre sin elegir (click afuera / Escape) → abandono (#9).
      closeFilterSession();
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeAll();
      }
    };
    const handleKeyDownGlobal = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAll();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDownGlobal);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDownGlobal);
    };
    // closeFilterSession solo usa refs/imports (sin estado), así que la closure
    // del primer render es correcta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reinicia el ítem resaltado al abrir/cerrar un dropdown o al filtrar.
  useEffect(() => {
    setActiveIndex(-1);
  }, [openDropdown, dropdownFilter]);

  // Mantiene visible la opción resaltada por teclado.
  useEffect(() => {
    if (activeIndex < 0 || !openDropdown) return;
    document.getElementById(`${openDropdown}-opt-${activeIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, openDropdown]);

  // Navegación de listas con teclado (flechas / Home / End / Enter) reutilizable
  // por los tres dropdowns.
  const handleListNav = <T,>(
    e: React.KeyboardEvent,
    options: T[],
    onSelect: (option: T) => void
  ) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (options.length === 0 ? -1 : i < 0 ? 0 : (i + 1) % options.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (options.length === 0 ? -1 : i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(options.length ? 0 : -1);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(options.length ? options.length - 1 : -1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = activeIndex >= 0 ? activeIndex : 0;
      if (options[idx] !== undefined) onSelect(options[idx]);
    }
  };

  // Función para normalizar el texto de los planes
  const normalizePlanText = (text: string | undefined | null): string => {
    if (!text || typeof text !== 'string') {
      return 'Texto no disponible';
    }

    let normalizedText = text
      .split(' ')
      .map(word => {
        if (!word) return word;
        // Preservar acrónimos all-caps con dígitos (MS1, MS2, etc.)
        if (/\d/.test(word) && word === word.toUpperCase()) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ')
      .replace(/ +/g, ' ')
      .replace(/ ?- ?/g, ' - ')
      .trim();

    // Formatear "Integra 2" por "Integra"
    normalizedText = normalizedText.replace(/Integra 2/gi, 'Integra');

    return normalizedText;
  };

  const renderBuscarPorField = () => (
    <div className={styles.fieldContainer}>
      <div
        className={`${styles.dropdown} ${openDropdown === "especialidad" ? styles.dropdownOpen : ""}`}
        onClick={() => handleDropdownToggle("especialidad")}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={openDropdown === "especialidad"}
        aria-controls="especialidad-listbox"
        aria-activedescendant={openDropdown === "especialidad" && activeIndex >= 0 ? `especialidad-opt-${activeIndex}` : undefined}
        onKeyDown={(e) => {
          if (openDropdown !== "especialidad") {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); handleDropdownToggle("especialidad"); }
          } else if (!showBuscarPorSearch) {
            handleListNav(e, filteredBuscarPor, selectBuscarPor);
          }
        }}
      >
        <div className={styles.dropdownContent}>
          <span className={`${styles.placeholderText} ${searchData.especialidad ? styles.hasSelectedText : ''}`}>Buscar por</span>
          {searchData.especialidad && (
            <span className={styles.selectedText}>{searchData.especialidad}</span>
          )}
        </div>
        <ArrowDown
          className={`${styles.chevron} ${openDropdown === "especialidad" ? styles.chevronRotated : ""}`}
          width={20}
          height={20}
        />
      </div>
      {openDropdown === "especialidad" && (
        <motion.div
          className={styles.dropdownMenu}
          onScroll={markFilterScrolled}
          role="listbox"
          id="especialidad-listbox"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {showBuscarPorSearch &&
            renderDropdownSearch("especialidad-listbox", "Buscar tipo…", filteredBuscarPor, selectBuscarPor)}
          {filteredBuscarPor.length > 0 ? (
            filteredBuscarPor.map((opcion, index) => (
              <div
                key={opcion}
                id={`especialidad-opt-${index}`}
                role="option"
                aria-selected={searchData.especialidad === opcion}
                className={`${styles.dropdownOption} ${activeIndex === index ? styles.dropdownOptionActive : ''}`}
                onClick={() => handleOptionSelect("especialidad", opcion)}
                onMouseEnter={() => setActiveIndex(index)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                {opcion}
                {searchData.especialidad === opcion && (
                  <BackgroundBlueCheckIcon style={{ marginLeft: 8 }} />
                )}
              </div>
            ))
          ) : (
            <div className={`${styles.dropdownOption} ${styles.dropdownHint}`}>
              Sin resultados para &quot;{dropdownFilter}&quot;
            </div>
          )}
        </motion.div>
      )}
    </div>
  );

  const resolveSelectedPlanText = () => {
    const isOdontologica = searchData.especialidad === "Especialidad odontológica";
    const source = isOdontologica ? planesOdont : planes;
    const match = source?.find((p: Plan) => p.CARTILLA === searchData.plan);
    if (match?.displayName) return normalizePlanText(match.displayName);
    if (isOdontologica) {
      const odontDisplay = ODONT_PLAN_DISPLAY_NAMES[searchData.plan];
      if (odontDisplay) return normalizePlanText(odontDisplay);
      return normalizePlanText(searchData.plan.replace(/^ODONTOLOGIA\s+/i, ''));
    }
    return normalizePlanText(searchData.plan);
  };

  const getPlansToShow = () => {
    const isOdontologica = searchData.especialidad === "Especialidad odontológica";
    const source = isOdontologica ? planesOdont : planes;
    if (!source) return source;
    return isOdontologica ? formatOdontPlansForDisplay(source) : sortPlansConectaLast(source);
  };

  const renderPlanOptions = () => {
    const isOdontologica = searchData.especialidad === "Especialidad odontológica";
    const isLoading = isOdontologica ? isLoadingPlanesOdont : isLoadingPlanes;
    const error = isOdontologica ? errorPlanesOdont : errorPlanes;

    if (isLoading) return <div className={styles.dropdownOption}>Cargando planes...</div>;
    if (error) return <div className={styles.dropdownOption}>Error al cargar planes</div>;

    if (!getPlansToShow()) return <div className={styles.dropdownOption}>No hay planes disponibles</div>;
    if (filteredPlans.length === 0) {
      return (
        <div className={`${styles.dropdownOption} ${styles.dropdownHint}`}>
          Sin resultados para &quot;{dropdownFilter}&quot;
        </div>
      );
    }

    return filteredPlans.map((plan: Plan, index: number) => (
      <div
        key={`${plan.CRT_CODIGO}-${plan.CARTILLA}-${index}`}
        id={`plan-opt-${index}`}
        role="option"
        aria-selected={searchData.plan === plan.CARTILLA}
        className={`${styles.dropdownOption} ${activeIndex === index ? styles.dropdownOptionActive : ''}`}
        onClick={() => handleOptionSelect("plan", plan.CARTILLA)}
        onMouseEnter={() => setActiveIndex(index)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {planDisplayText(plan)}
        {searchData.plan === plan.CARTILLA && (
          <BackgroundBlueCheckIcon style={{ marginLeft: 8 }} />
        )}
      </div>
    ));
  };

  const renderPlanField = () => (
    <div className={styles.fieldContainer}>
      {/* El plan queda siempre habilitado: Salud mental también lo necesita para
          mostrar el contenido correcto (tradicional vs MS1/MS2). */}
      <div
        className={`${styles.dropdown} ${openDropdown === "plan" ? styles.dropdownOpen : ""}`}
        onClick={() => handleDropdownToggle("plan")}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={openDropdown === "plan"}
        aria-controls="plan-listbox"
        aria-activedescendant={openDropdown === "plan" && activeIndex >= 0 ? `plan-opt-${activeIndex}` : undefined}
        onKeyDown={(e) => {
          if (openDropdown !== "plan") {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); handleDropdownToggle("plan"); }
          } else if (!showPlanSearch) {
            handleListNav(e, filteredPlans, selectPlan);
          }
        }}
      >
        <div className={styles.dropdownContent}>
          <span className={`${styles.placeholderText} ${searchData.plan ? styles.hasSelectedText : ''}`}>Elegí tu plan</span>
          {searchData.plan && (
            <span className={styles.selectedText}>{resolveSelectedPlanText()}</span>
          )}
        </div>
        <ArrowDown
          className={`${styles.chevron} ${openDropdown === "plan" ? styles.chevronRotated : ""}`}
          width={20}
          height={20}
        />
      </div>
      {openDropdown === "plan" && (
        <motion.div
          className={styles.dropdownMenu}
          onScroll={markFilterScrolled}
          role="listbox"
          id="plan-listbox"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {showPlanSearch &&
            renderDropdownSearch("plan-listbox", "Buscar plan…", filteredPlans, selectPlan)}
          {renderPlanOptions()}
        </motion.div>
      )}
    </div>
  );

  const renderLocationSuggestions = () => {
    if (isLoadingLocations) return <div className={styles.dropdownOption}>Buscando...</div>;
    // El backend de /Locations/PlanWeb/{plan} no restringe estrictamente a CABA
    // para Conecta (localidades de GBA linderas también matchean), así que se
    // filtra en el cliente (VEN-395).
    const visibleLocations = isConectaPlan(searchData.plan)
      ? locations?.filter((location: Location | undefined | null) => location && isCabaLocation(location))
      : locations;
    if (visibleLocations && visibleLocations.length > 0) {
      return visibleLocations
        .filter((location: Location | undefined | null) => location !== undefined && location !== null)
        .map((location: Location, index: number) => (
          <div
            key={`${location.SYSTEMLOCATION}-${location.PROVINCIA}-${index}`}
            className={styles.dropdownOption}
            onClick={() => handleLocationSelect(location)}
          >
            {formatLocationDisplay(location)}
          </div>
        ));
    }
    if (searchData.nombreApellido.length >= 1) {
      return <div className={styles.dropdownOption}>No se encontraron resultados</div>;
    }
    return null;
  };

  const renderLocationField = (wide = false) => {
    // VEN-395: con Conecta, Pcia/Zona queda fija en Capital Federal (la
    // auto-selecciona el useEffect de arriba) y no se puede tipear ni volver
    // a elegir otra zona, igual que pasa con Salud mental (isDisabled).
    const locationLocked = isDisabled || isConectaPlan(searchData.plan);

    return (
    <div
      className={`${styles.fieldContainer} location-input-container ${wide ? styles.wideField : ''}`}
      style={{ position: 'relative' }}
    >
      {selectedLocationData ? (
        <div
          className={`${styles.textInputContainer} ${locationLocked ? styles.disabled : ""}`}
          onClick={() => {
            if (locationLocked) return;
            handleInputChange("nombreApellido", "");
            setSelectedLocationData(undefined);
            onLocationSelect?.(null);
            onUserCoords?.(null);
            setGeoError("");
            setShowLocationSuggestions(true);
          }}
        >
          <span className={styles.textInputTitle}>Pcia / Zona</span>
          <span className={styles.textInputValue}>{searchData.nombreApellido}</span>
        </div>
      ) : (
        <input
          type="text"
          className={`${styles.textInput} ${locationLocked ? styles.textInputDisabled : ""} ${!searchData.nombreApellido ? styles.locationInputWithGps : ""}`}
          placeholder="Pcia / Zona"
          value={searchData.nombreApellido}
          onChange={e => {
            if (locationLocked) return;
            handleInputChange("nombreApellido", e.target.value);
            setShowLocationSuggestions(true);
            markFilterTyped();
          }}
          onFocus={() => {
            if (locationLocked) return;
            setOpenDropdown(null);
            setShowLocationSuggestions(true);
            openFilterSession('ubicacion'); // #7
          }}
          onKeyDown={handleKeyDown}
          disabled={locationLocked}
        />
      )}

      {!locationLocked && !selectedLocationData && !searchData.nombreApellido && (
        <button
          type="button"
          className={styles.gpsButton}
          onClick={handleUseMyLocation}
          disabled={isLocating}
          title="Buscar prestadores cerca de tu ubicación actual"
        >
          <LocationCrosshairIcon />
          {isLocating ? "Detectando…" : "Mi ubicación"}
        </button>
      )}

      {geoError && <span className={styles.gpsError}>{geoError}</span>}

      {!locationLocked && showLocationSuggestions && searchData.nombreApellido && searchData.plan && (
        <motion.div
          className={styles.dropdownMenu}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000 }}
          onScroll={markFilterScrolled}
        >
          {renderLocationSuggestions()}
        </motion.div>
      )}
    </div>
    );
  };

  const renderEspecialidadSecundariaField = () => (
    <div className={styles.fieldContainer}>
      <div
        className={`${styles.dropdown} ${openDropdown === "especialidadSecundaria" ? styles.dropdownOpen : ""} ${isDisabled ? styles.disabled : ""}`}
        onClick={() => !isDisabled && handleDropdownToggle("especialidadSecundaria")}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={openDropdown === "especialidadSecundaria"}
        aria-controls="especialidadSecundaria-listbox"
        aria-activedescendant={
          openDropdown === "especialidadSecundaria" && !showSpecialtySearch && activeIndex >= 0
            ? `especialidadSecundaria-opt-${activeIndex}`
            : undefined
        }
        onKeyDown={(e) => {
          if (isDisabled) return;
          if (openDropdown !== "especialidadSecundaria") {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); handleDropdownToggle("especialidadSecundaria"); }
          } else if (!showSpecialtySearch) {
            handleListNav(e, filteredSpecialties, selectSpecialty);
          }
        }}
      >
        <div className={styles.dropdownContent}>
          <span className={`${styles.placeholderText} ${searchData.especialidadSecundaria ? styles.hasSelectedText : ''}`}>Especialidad</span>
          {searchData.especialidadSecundaria && (
            <span className={styles.selectedText}>{normalizePlanText(searchData.especialidadSecundaria)}</span>
          )}
        </div>
        <ArrowDown
          className={`${styles.chevron} ${openDropdown === "especialidadSecundaria" ? styles.chevronRotated : ""}`}
          width={20}
          height={20}
        />
      </div>
      {openDropdown === "especialidadSecundaria" && renderSpecialtiesDropdown()}
    </div>
  );

  // Selección de una sugerencia de nombre: fija el nombre elegido (que luego
  // viaja como vTexto a la búsqueda) y cierra el listado.
  const selectProfessionalSuggestion = (name: string) => {
    setSelectedProfessionalName(name);
    // Reseteamos la especialidad: pasa a acotarse a las del profesional elegido.
    setSearchData(prev => ({ ...prev, nombreApellido: name, especialidadSecundaria: "" }));
    setShowProfessionalSuggestions(false);
  };

  const renderProfessionalSuggestionsList = () => {
    if (isLoadingSuggestions) return <div className={styles.dropdownOption}>Buscando…</div>;
    if (professionalSuggestions && professionalSuggestions.length > 0) {
      return professionalSuggestions.map((name) => (
        <div
          key={name}
          className={styles.dropdownOption}
          onClick={() => selectProfessionalSuggestion(name)}
        >
          {name}
        </div>
      ));
    }
    return <div className={`${styles.dropdownOption} ${styles.dropdownHint}`}>Sin coincidencias</div>;
  };

  const renderProfessionalNameField = () => (
    <div className={styles.fieldContainer} style={{ position: 'relative' }}>
      {selectedProfessionalName ? (
        <div
          className={`${styles.textInputContainer} ${isDisabled ? styles.disabled : ""}`}
          onClick={() => {
            if (isDisabled) return;
            // Al limpiar el profesional, la especialidad vuelve a la lista genérica.
            setSearchData(prev => ({ ...prev, nombreApellido: "", especialidadSecundaria: "" }));
            setSelectedProfessionalName(undefined);
            setShowProfessionalSuggestions(true);
          }}
        >
          <span className={styles.textInputTitle}>Nombre del profesional</span>
          <span className={styles.textInputValue}>{selectedProfessionalName}</span>
        </div>
      ) : (
        <input
          type="text"
          className={`${styles.textInput} ${isDisabled ? styles.disabled : ""}`}
          placeholder="Nombre del profesional"
          value={searchData.nombreApellido}
          onChange={e => {
            if (isDisabled) return;
            handleInputChange("nombreApellido", e.target.value);
            setShowProfessionalSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (isDisabled) return;
            setOpenDropdown(null);
            setShowLocationSuggestions(false);
            setShowProfessionalSuggestions(true);
          }}
          disabled={isDisabled}
        />
      )}

      {showProfessionalSuggestions && !selectedProfessionalName &&
        searchData.plan && searchData.nombreApellido.trim().length >= MIN_SUGGESTION_LENGTH && (
        <motion.div
          className={styles.dropdownMenu}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000 }}
        >
          {renderProfessionalSuggestionsList()}
        </motion.div>
      )}
    </div>
  );

  const formatLocationDisplay = (location: Location | undefined | null): string => {
    try {
      // Validar que location existe y tiene las propiedades necesarias
      if (!location || typeof location !== 'object') {
        return 'Localidad no disponible';
      }

      const userLocation = location.USERLOCATION?.trim() || '';
      const systemLocation = location.SYSTEMLOCATION?.trim() || '';
      const provincia = location.PROVINCIA?.trim() || '';

      let displayText = '';

      // Usar USERLOCATION si está disponible, sino combinar SYSTEMLOCATION con PROVINCIA
      if (userLocation) {
        displayText = userLocation.replace(/\s+/g, ' ').trim();
      } else if (systemLocation && provincia) {
        displayText = `${systemLocation}, ${provincia}`;
      } else {
        displayText = systemLocation || provincia || 'Localidad no disponible';
      }

      // Normalizar el texto (capitalización consistente)
      return normalizePlanText(displayText);
    } catch (error) {
      console.error('Error formateando localidad:', error, location);
      return 'Localidad no disponible';
    }
  };

  const isSearchDisabled = isSearchButtonDisabled();

  // Listas de opciones (a nivel componente para que la navegación por teclado y
  // el render compartan exactamente los mismos ítems e índices).
  const normalizeForFilter = (s: string | undefined | null) =>
    (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  // Umbral a partir del cual un dropdown muestra el buscador interno por tipeo.
  const SEARCH_THRESHOLD = 6;

  // "Especialidad" (secundaria): las opciones vienen del backend; se ordenan
  // alfabéticamente y luego se filtran por lo tipeado.
  const sortedSpecialties = [...specialtiesSource].sort((a: Specialty, b: Specialty) =>
    normalizeForFilter(a.ESPECIALIDAD).localeCompare(normalizeForFilter(b.ESPECIALIDAD))
  );
  const filteredSpecialties = sortedSpecialties.filter((sp: Specialty) =>
    normalizeForFilter(sp.ESPECIALIDAD).includes(normalizeForFilter(dropdownFilter))
  );
  const showSpecialtySearch =
    !!(searchData.plan && searchData.especialidad) && !isLoadingSpecialtiesSource && specialtiesSource.length > SEARCH_THRESHOLD;

  // "Buscar por": lista fija ordenada alfabéticamente y filtrada por tipeo.
  const sortedBuscarPor = [...buscarPorOpciones].sort((a, b) =>
    normalizeForFilter(a).localeCompare(normalizeForFilter(b))
  );
  const filteredBuscarPor = sortedBuscarPor.filter((opcion) =>
    normalizeForFilter(opcion).includes(normalizeForFilter(dropdownFilter))
  );
  const showBuscarPorSearch = buscarPorOpciones.length > SEARCH_THRESHOLD;

  // "Plan": se filtra por el texto visible (displayName / CARTILLA normalizado).
  const plansToShow = (getPlansToShow() ?? []) as Plan[];
  const planDisplayText = (plan: Plan) => normalizePlanText(plan.displayName || plan.CARTILLA);
  const filteredPlans = plansToShow.filter((plan) =>
    normalizeForFilter(planDisplayText(plan)).includes(normalizeForFilter(dropdownFilter))
  );
  const showPlanSearch = plansToShow.length > SEARCH_THRESHOLD;

  const selectBuscarPor = (opcion: string) => handleOptionSelect("especialidad", opcion);
  const selectPlan = (plan: Plan) => handleOptionSelect("plan", plan.CARTILLA);
  const selectSpecialty = (sp: Specialty) => handleOptionSelect("especialidadSecundaria", sp.ESPECIALIDAD || "");

  // Tipeo en el buscador interno del dropdown abierto, debounced (#8).
  // hubo_match según la lista filtrada que ve el usuario en ese momento.
  useEffect(() => {
    const texto = dropdownFilter.trim();
    const s = filterSessionRef.current;
    if (!texto || !s || s.filtro === 'ubicacion' || !openDropdown) return;
    const matches =
      openDropdown === 'plan' ? filteredPlans.length
        : openDropdown === 'especialidad' ? filteredBuscarPor.length
          : filteredSpecialties.length;
    const timer = setTimeout(() => {
      if (filterSessionRef.current?.filtro !== s.filtro) return;
      recordFilterTyped(texto, matches > 0);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownFilter]);

  // Input de búsqueda reutilizable dentro de un dropdown (queda fijo arriba).
  const renderDropdownSearch = <T,>(
    listboxId: string,
    placeholder: string,
    options: T[],
    onSelect: (option: T) => void
  ) => (
    <div className={styles.dropdownSearch} onClick={(e) => e.stopPropagation()}>
      <Search width={16} height={16} />
      <input
        autoFocus
        type="text"
        className={styles.dropdownSearchInput}
        placeholder={placeholder}
        value={dropdownFilter}
        role="combobox"
        aria-expanded
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 && openDropdown ? `${openDropdown}-opt-${activeIndex}` : undefined}
        onChange={(e) => setDropdownFilter(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setOpenDropdown(null); return; }
          handleListNav(e, options, onSelect);
        }}
      />
    </div>
  );

  return (
    <div className={`${styles.container} ${className}`} ref={containerRef}>
      <div className={styles.searchBar}>
        {renderBuscarPorField()}
        <div className={styles.divider}></div>
        {renderPlanField()}

        {ESPECIALIDADES_CON_LOCATION_Y_SECUNDARIA.has(searchData.especialidad) && (
          <>
            <div className={styles.divider}></div>
            {renderEspecialidadSecundariaField()}
            <div className={styles.divider}></div>
            {renderLocationField()}
          </>
        )}

        {searchData.especialidad === "Nombre del profesional" && (
          <>
            <div className={styles.divider}></div>
            {renderProfessionalNameField()}
            <div className={styles.divider}></div>
            {renderEspecialidadSecundariaField()}
          </>
        )}

        {ESPECIALIDADES_SOLO_LOCATION_WIDE.has(searchData.especialidad) && (
          <>
            <div className={styles.divider}></div>
            {renderLocationField(true)}
          </>
        )}

        {searchData.especialidad === "" && (
          <>
            <div className={styles.divider}></div>
            {renderEspecialidadSecundariaField()}
            <div className={styles.divider}></div>
            {renderLocationField()}
          </>
        )}

        <div className={styles.divider}></div>

        <motion.button
          className={`${styles.searchButton} ${isSearchDisabled ? styles.disabled : ""}`}
          onClick={() => handleSearch()}
          disabled={isSearchDisabled}
          whileHover={{ scale: isSearchDisabled ? 1 : 1.02 }}
          whileTap={{ scale: isSearchDisabled ? 1 : 0.98 }}
        >
          {isLoadingProfessionals ? <span>. . .</span> :
            (<span>
              <Search width={16} height={16} />
              Buscar
            </span>)}
        </motion.button>
      </div>
    </div>
  )
})
